import React, { useState } from 'react';
import { DollarSignIcon, TrendingUpIcon, ShieldIcon, GlobeIcon, BarChartIcon, BuildingIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
const StrategySection = () => {
  // State to track which strategy card is expanded
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  // Enhanced strategy data with more detailed information
  const strategies = [{
    icon: <ChevronDownIcon className="w-8 h-8 text-gold-500" data-id="element-241" />,
    title: 'INFASTRUCTURE',
    description: 'High Performance Personal and Professional Coaching & Community',
    benefits: ['Access Business and Life Coaching for mental clarity and focus', 'Personal training to improve physical health and energy', 'Marketing and Branding for Business Exposure', 'Premium Personalized Brand Gifts'],
    example: "Through guided tools and resources, you'll create a strong foundation that elevates both your personal and business brand.",
    metric: {
      value: 'Stronger performance in life and business.',
      label: ''
    },
    variant: 'gold'
  }, {
    icon: <DollarSignIcon className="w-8 h-8 text-gold-500" data-id="element-242" />,
    title: 'FUNDING',
    description: 'Accredited Investor Access',
    benefits: ['Access funding options with favorable terms and lower barriers', 'Leveraged strategic partnerships with funding institutions'],
    example: 'Our clients typically secure 30-50% more funding with our accredited investor network than through traditional channels.',
    metric: {
      value: '2.4x',
      label: 'Average funding increase'
    },
    variant: 'gold'
  }, {
    icon: <TrendingUpIcon className="w-8 h-8 text-gold-500" data-id="element-243" />,
    title: 'CAPITAL ACCELERATION',
    description: 'Private Equity Optimization',
    benefits: ['Maximize cash flow through strategic capital management', 'Create passive income streams through optimized investment vehicles', 'Balance risk and return with our proprietary framework'],
    example: 'Through our Capital Acceleration strategy implementation, clients have achieved double digit returns on optimized private equity positions.',
    metric: {
      value: 'Double Digit',
      label: 'Returns on optimized capital strategies'
    },
    variant: 'gold'
  }, {
    icon: <ShieldIcon className="w-8 h-8 text-gold-500" data-id="element-244" />,
    title: 'ASSET PROTECTION',
    description: 'Asset Protection Systems',
    benefits: ['Shield your wealth from potential legal and financial threats', 'Create structured protection for your family and business interests', 'Implement "Becoming Your Own Bank" strategies for greater control'],
    example: 'Our B.Y.O.B system has helped clients protect assets while maintaining liquidity and growth potential through strategic structuring.',
    metric: {
      value: '100%',
      label: 'Asset protection coverage'
    },
    variant: 'gold'
  }, {
    icon: <BuildingIcon className="w-8 h-8 text-gold-500" data-id="element-245" />,
    title: 'ASSET ACQUISITION',
    description: 'Strategic Investments',
    benefits: ['Identify and acquire high-value assets with strategic advantage', 'Build a diverse portfolio of cash-flowing assets'],
    example: 'Through our acquisition framework, clients have successfully acquired businesses and properties at 15-20% below market value.',
    metric: {
      value: '18%',
      label: 'Average below-market acquisition'
    },
    variant: 'gold'
  }, {
    icon: <BarChartIcon className="w-8 h-8 text-gold-500" data-id="element-246" />,
    title: 'PROFIT PROTECTION',
    description: 'Wealth Accumulation',
    benefits: ['Implement advanced profit acceleration techniques', 'Optimize tax strategies for maximum wealth retention', 'Create compound growth systems that work while you sleep'],
    example: 'Our wealth accumulation framework has helped clients increase net worth by an average of 40% within the first 24 months.',
    metric: {
      value: '40%',
      label: 'Avg. net worth increase'
    },
    variant: 'gold'
  }, {
    icon: <GlobeIcon className="w-8 h-8 text-gold-500" data-id="element-247" />,
    title: 'EXPANSION & SUCCESSION',
    description: 'Global Growth Strategies',
    benefits: ['Scale your wealth across borders and markets', 'Diversify investments with international opportunities', 'Access global networks and partnerships'],
    example: 'Clients implementing our expansion strategies have successfully entered an average of 3 new markets within 18 months.',
    metric: {
      value: '3x',
      label: 'Average market reach increase'
    },
    variant: 'gold'
  }];
  const toggleCard = (index: number) => {
    if (expandedCard === index) {
      setExpandedCard(null);
    } else {
      setExpandedCard(index);
    }
  };
  return <section id="strategy" className="py-20 bg-gradient-to-b from-gold-50 to-white pt-[30px] pb-[30px]" data-id="element-248">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-249">
        <h2 className="text-4xl font-bold mb-4 section-title-decoration" data-id="element-250">
          Legacy Lab <span className="text-gradient" data-id="element-251">Strategy</span>
        </h2>
        <p className="max-w-3xl mx-auto text-lg text-gray-600 mb-16" data-id="element-252">
          Our comprehensive approach combines proven financial strategies with
          innovative wealth-building techniques, all designed to accelerate your
          path to financial freedom.
        </p>
        {/* First row - 3 cards */}
        <div className="relative mb-16" data-id="element-253">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10" data-id="element-254">
            {strategies.slice(0, 3).map((strategy, index) => <div key={index} className="strategy-card group" onClick={() => toggleCard(index)} data-id="element-255">
                {/* Card content */}
                <div className="flex justify-center mb-4" data-id="element-256">
                  <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300" data-id="element-257">
                    {strategy.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-258">
                  {strategy.title}
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-259">{strategy.description}</p>
                {/* Expand/collapse indicator */}
                <div className="flex items-center justify-center mt-2 text-gold-500" data-id="element-260">
                  <span className="text-sm mr-1" data-id="element-261">
                    {expandedCard === index ? 'Show less' : 'Learn more'}
                  </span>
                  {expandedCard === index ? <ChevronUpIcon className="w-4 h-4" data-id="element-262" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-263" />}
                </div>
                {/* Expanded content */}
                {expandedCard === index && <div className="mt-6 pt-6 border-t border-gray-100 text-left animate-fadeIn" data-id="element-264">
                    {/* Benefits */}
                    <h4 className="text-sm font-medium text-gold-600 mb-2" data-id="element-265">
                      Benefits
                    </h4>
                    <ul className="text-sm space-y-2 mb-4" data-id="element-266">
                      {strategy.benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-267">
                          <span className="text-gold-500 mr-2" data-id="element-268">•</span>
                          <span className="text-gray-700" data-id="element-269">{benefit}</span>
                        </li>)}
                    </ul>
                    {/* Example */}
                    <h4 className="text-sm font-medium text-gold-600 mb-2" data-id="element-270">
                      How It Works
                    </h4>
                    <p className="text-sm text-gray-700 mb-4" data-id="element-271">
                      {strategy.example}
                    </p>
                    {/* Success metric */}
                    <div className="bg-gold-50 p-3 rounded-lg text-center mb-4" data-id="element-272"></div>
                  </div>}
              </div>)}
          </div>
          {/* Connector line between rows */}
          <div className="connector-line" data-id="element-273"></div>
        </div>
        {/* Second row - ASSET PROTECTION, ASSET ACQUISITION, PROFIT */}
        <div className="relative mb-16" data-id="element-274">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10" data-id="element-275">
            {strategies.slice(3, 6).map((strategy, index) => <div key={index + 3} className={strategy.variant === 'navy' ? 'strategy-card-navy group' : 'strategy-card group'} onClick={() => toggleCard(index + 3)} data-id="element-276">
                {/* Card content */}
                <div className="flex justify-center mb-4" data-id="element-277">
                  <div className={`w-16 h-16 ${strategy.variant === 'navy' ? 'bg-navy-100' : 'bg-gold-100'} rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300`} data-id="element-278">
                    {strategy.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-279">
                  {strategy.title}
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-280">{strategy.description}</p>
                {/* Expand/collapse indicator */}
                <div className="flex items-center justify-center mt-2 text-gold-500" data-id="element-281">
                  <span className="text-sm mr-1" data-id="element-282">
                    {expandedCard === index + 3 ? 'Show less' : 'Learn more'}
                  </span>
                  {expandedCard === index + 3 ? <ChevronUpIcon className="w-4 h-4" data-id="element-283" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-284" />}
                </div>
                {/* Expanded content */}
                {expandedCard === index + 3 && <div className="mt-6 pt-6 border-t border-gray-100 text-left animate-fadeIn" data-id="element-285">
                    {/* Benefits */}
                    <h4 className={`text-sm font-medium ${strategy.variant === 'navy' ? 'text-navy-600' : 'text-gold-600'} mb-2`} data-id="element-286">
                      Benefits
                    </h4>
                    <ul className="text-sm space-y-2 mb-4" data-id="element-287">
                      {strategy.benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-288">
                          <span className={strategy.variant === 'navy' ? 'text-navy-500 mr-2' : 'text-gold-500 mr-2'} data-id="element-289">
                            •
                          </span>
                          <span className="text-gray-700" data-id="element-290">{benefit}</span>
                        </li>)}
                    </ul>
                    {/* Example */}

                    {/* Success metric */}
                    <div className={`${strategy.variant === 'navy' ? 'bg-navy-50' : 'bg-gold-50'} p-3 rounded-lg text-center mb-4`} data-id="element-291"></div>
                  </div>}
              </div>)}
          </div>
          {/* Connector line between rows */}
          <div className="connector-line" data-id="element-292"></div>
        </div>
        {/* Third row - Just EXPANSION centered */}
        <div className="relative" data-id="element-293">
          <div className="flex justify-center relative z-10" data-id="element-294">
            <div className="w-full md:w-1/3" data-id="element-295">
              <div key={6} className="strategy-card group" onClick={() => toggleCard(6)} data-id="element-296">
                {/* Card content */}
                <div className="flex justify-center mb-4" data-id="element-297">
                  <div className="w-16 h-16 bg-gold-100 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300" data-id="element-298">
                    {strategies[6].icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-299">
                  {strategies[6].title}
                </h3>
                <p className="text-gray-600 mb-4" data-id="element-300">
                  {strategies[6].description}
                </p>
                {/* Expand/collapse indicator */}
                <div className="flex items-center justify-center mt-2 text-gold-500" data-id="element-301">
                  <span className="text-sm mr-1" data-id="element-302">
                    {expandedCard === 6 ? 'Show less' : 'Learn more'}
                  </span>
                  {expandedCard === 6 ? <ChevronUpIcon className="w-4 h-4" data-id="element-303" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-304" />}
                </div>
                {/* Expanded content */}
                {expandedCard === 6 && <div className="mt-6 pt-6 border-t border-gray-100 text-left animate-fadeIn" data-id="element-305">
                    {/* Benefits */}
                    <h4 className="text-sm font-medium text-gold-600 mb-2" data-id="element-306">
                      Benefits
                    </h4>
                    <ul className="text-sm space-y-2 mb-4" data-id="element-307">
                      {strategies[6].benefits.map((benefit, i) => <li key={i} className="flex items-start" data-id="element-308">
                          <span className="text-gold-500 mr-2" data-id="element-309">•</span>
                          <span className="text-gray-700" data-id="element-310">{benefit}</span>
                        </li>)}
                    </ul>
                    {/* Example */}
                    <h4 className="text-sm font-medium text-gold-600 mb-2" data-id="element-311">
                      How It Works
                    </h4>
                    <p className="text-sm text-gray-700 mb-4" data-id="element-312">
                      {strategies[6].example}
                    </p>
                    {/* Success metric */}
                    <div className="bg-gold-50 p-3 rounded-lg text-center mb-4" data-id="element-313">
                      <span className="text-xl font-bold text-gold-600" data-id="element-314">
                        {strategies[6].metric.value}
                      </span>
                      <p className="text-xs text-gray-600" data-id="element-315">
                        {strategies[6].metric.label}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default StrategySection;